import { BanknoteIcon, MapPinnedIcon, NetworkIcon, PackageCheckIcon } from 'lucide-react';
import { Link } from 'react-router';

import type { OrderDTO } from '@invenco/common-interface/sales';
import type { ShipmentDTO } from '@invenco/shipping-interface';

import { Button } from '@/components/core/button';
import { Skeleton } from '@/components/core/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/core/tabs';
import { DetailList } from '@/components/detail-list';
import { EMPTY_TEXT_VALUE } from '@/constants';

import { OrderTimeline } from './order-timeline/order-timeline';

type Props = {
  order?: OrderDTO;
  shipments?: ShipmentDTO[];
  isLoading?: boolean;
};

function TextTabContent({ value, isLoading }: { value?: string | null; isLoading?: boolean }) {
  if (isLoading) {
    return <Skeleton className="h-4 w-full" aria-label="Loading value" />;
  }

  return (
    <p className="wrap-break-word whitespace-pre-wrap">
      {value ?? <span className="text-muted-foreground">{EMPTY_TEXT_VALUE}</span>}
    </p>
  );
}

function getChannelValue(order?: OrderDTO) {
  if (!order?.channelName) return undefined;

  if (!order.channelId) return order.channelName;

  return (
    <Button variant="link" size="inline" asChild>
      <Link to={`/sales/channels/${order.channelId}`}>{order.channelName}</Link>
    </Button>
  );
}

export function OrderDetailTabs({ order, shipments, isLoading }: Props) {
  return (
    <Tabs defaultValue="timeline" className="gap-5">
      <TabsList variant="button" aria-label="Order details">
        <TabsTrigger value="properties">Properties</TabsTrigger>
        <TabsTrigger value="timeline">Timeline</TabsTrigger>
        <TabsTrigger value="reference">Reference</TabsTrigger>
        <TabsTrigger value="notes">Notes</TabsTrigger>
      </TabsList>
      <TabsContent value="properties">
        <DetailList
          isLoading={isLoading}
          items={[
            { icon: NetworkIcon, label: 'Channel', value: getChannelValue(order) },
            { icon: BanknoteIcon, label: 'Currency', value: order?.currency },
            {
              icon: PackageCheckIcon,
              label: 'Partial fulfillment',
              value: order?.allowPartialFulfillments ? 'Enabled' : 'Disabled',
            },
            {
              icon: MapPinnedIcon,
              label: 'Fulfillment profile',
              value: order?.fulfillmentProfileName,
            },
          ]}
        />
      </TabsContent>
      <TabsContent value="timeline">
        <OrderTimeline order={order} shipments={shipments} isLoading={isLoading} />
      </TabsContent>
      <TabsContent value="reference">
        <TextTabContent value={order?.reference} isLoading={isLoading} />
      </TabsContent>
      <TabsContent value="notes">
        <TextTabContent value={order?.notes} isLoading={isLoading} />
      </TabsContent>
    </Tabs>
  );
}
